import React, { useState, useEffect, useRef } from "react";
import { IonContent, IonText, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const emojis = ["☀️", "🌧️", "🎈", "🏀", "🦋", "🌲", "🚀"];

const TopBottom: React.FC = () => {
    const [emoji, setEmoji] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const generateNewQuestion = () => {
        const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        const randomPosition = Math.random() < 0.5 ? "Top" : "Bottom";
        setEmoji(randomEmoji);
        setPosition(randomPosition);
        setShowImage(false);
        setError(false);
    };

    useEffect(() => {
        generateNewQuestion();
        document.title = "Top and Bottom Worksheets for UKG | Spatial Concept Activities";
    }, []);

    const handleAnswer = (answer: string) => {
        if (answer === position) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
            }, 3000);
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG children learn top and bottom positions with fun worksheets and real-world examples. Improve spatial awareness through engaging printable activities." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/top-bottom" />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord MoneyDiv">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Top and Bottom</h1>

                            <p>
                                The concepts of <strong>top</strong> and <strong>bottom</strong> are essential for helping children understand positions and spatial relationships. In UKG, these foundational position words prepare kids for early math and daily life instructions. Mama’s Guidance offers <strong>top and bottom worksheets</strong> that are interactive, visual, and easy to understand.
                            </p>

                            <h2>🎯 What Are Top and Bottom?</h2>
                            <p>
                                <strong>Top</strong> refers to the highest part or the uppermost position, while <strong>bottom</strong> refers to the lowest or base position of something.
                            </p>

                            <ul>
                                <li>The star is at the <strong>top</strong> of the tree.</li>
                                <li>The shoes are kept at the <strong>bottom</strong> of the shelf.</li>
                                <li>The sun is at the <strong>top</strong> of the sky, and grass is at the <strong>bottom</strong> of the garden.</li>
                            </ul>

                            <h2>🧠 Learning Objectives</h2>
                            <ul>
                                <li>Identify top and bottom positions in pictures</li>
                                <li>Understand spatial arrangement using vertical alignment</li>
                                <li>Use position words in real-life situations</li>
                                <li>Strengthen visual observation and direction-following skills</li>
                            </ul>

                            <h2>🖍️ What’s in the Worksheets?</h2>
                            <ul>
                                <li>Circle the object at the top/bottom of a group</li>
                                <li>Match items placed at the top and bottom</li>
                                <li>Color objects based on position</li>
                                <li>Complete simple scenes with objects on top/bottom shelves</li>
                                <li>Sorting activities: top vs. bottom rows</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents and Educators</h2>
                            <ul>
                                <li>Use a real shelf or cupboard to demonstrate top and bottom positions</li>
                                <li>Stack books or blocks and ask questions like <strong>“What is on top?”</strong></li>
                                <li>Practice during dressing up: <strong>“Put your cap on top”</strong></li>
                                <li>Use storytelling to include spatial clues like “the bird sat on the top branch”</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Long - Short</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Heavy - Light</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Front - Back</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Involve your child in simple home activities like organizing shoes or books. Ask them to describe which item is at the top or bottom to strengthen vocabulary and understanding.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Top and Bottom</h1>

                        <p>
                            The concepts of <strong>top</strong> and <strong>bottom</strong> are essential for helping children understand positions and spatial relationships. In UKG, these foundational position words prepare kids for early math and daily life instructions. Mama’s Guidance offers <strong>top and bottom worksheets</strong> that are interactive, visual, and easy to understand.
                        </p>

                        <h2>🎯 What Are Top and Bottom?</h2>
                        <p>
                            <strong>Top</strong> refers to the highest part or the uppermost position, while <strong>bottom</strong> refers to the lowest or base position of something.
                        </p>

                        <ul>
                            <li>The star is at the <strong>top</strong> of the tree.</li>
                            <li>The shoes are kept at the <strong>bottom</strong> of the shelf.</li>
                            <li>The sun is at the <strong>top</strong> of the sky, and grass is at the <strong>bottom</strong> of the garden.</li>
                        </ul>

                        <h2>🧠 Learning Objectives</h2>
                        <ul>
                            <li>Identify top and bottom positions in pictures</li>
                            <li>Understand spatial arrangement using vertical alignment</li>
                            <li>Use position words in real-life situations</li>
                            <li>Strengthen visual observation and direction-following skills</li>
                        </ul>

                        <h2>🖍️ What’s in the Worksheets?</h2>
                        <ul>
                            <li>Circle the object at the top/bottom of a group</li>
                            <li>Match items placed at the top and bottom</li>
                            <li>Color objects based on position</li>
                            <li>Complete simple scenes with objects on top/bottom shelves</li>
                            <li>Sorting activities: top vs. bottom rows</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents and Educators</h2>
                        <ul>
                            <li>Use a real shelf or cupboard to demonstrate top and bottom positions</li>
                            <li>Stack books or blocks and ask questions like <strong>“What is on top?”</strong></li>
                            <li>Practice during dressing up: <strong>“Put your cap on top”</strong></li>
                            <li>Use storytelling to include spatial clues like “the bird sat on the top branch”</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Long - Short</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Heavy - Light</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Front - Back</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Involve your child in simple home activities like organizing shoes or books. Ask them to describe which item is at the top or bottom to strengthen vocabulary and understanding.
                        </p>
                    </article>
                    <div className="text-center">
                        <h2>Where is the {emoji} placed?</h2>
                    </div>
                    <div style={{ textAlign: "center", fontSize: "50px", margin: "20px 0" }}>
                        {position === "Top" && <div>{emoji}</div>}
                        <div>🏠</div>
                        {position === "Bottom" && <div>{emoji}</div>}
                    </div>
                    <div className="optionDiv text-center mt-2">
                        <IonButton color="primary" onClick={() => handleAnswer("Top")}>
                            Top
                        </IonButton>
                        <IonButton color="secondary" onClick={() => handleAnswer("Bottom")}>
                            Bottom
                        </IonButton>
                    </div>
                    {error && <p style={{ color: "red" }}>Try again!</p>}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well Done"
                    />
                </div>
            )}
        </>
    );
};

export default TopBottom;
